import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AdminEmailOutboxListDto,
    AdminEmailPreviewResultDto,
    AdminEmailTemplateDetailDto,
    AdminEmailTemplateListDto,
    getAdminEmailOutbox,
    getAdminEmailTemplate,
    getAdminEmailTestMode,
    getAllAdminEmailTemplates,
    previewAdminEmailTemplate,
    testSendAdminEmailTemplate,
    updateAdminEmailDeliveryPolicy,
    updateAdminEmailTemplateLocale,
    updateAdminEmailTestMode,
} from '../../services/api.admin';
import { ValidationError } from './ValidationError';
import { useBackendAuth } from '../../hooks/useAuth';

type Tab = 'templates' | 'outbox';

/** Hidden QA panel: click page title this many times to reveal (dev/local only UX). */
const TEST_MODE_REVEAL_CLICKS = 10;

const EmailTemplatesPanel = () => {
    const { user } = useBackendAuth();
    const [tab, setTab] = useState<Tab>('templates');
    const [list, setList] = useState<AdminEmailTemplateListDto[] | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [detail, setDetail] = useState<AdminEmailTemplateDetailDto | null>(null);
    const [locale, setLocale] = useState('en');
    const [subject, setSubject] = useState('');
    const [htmlBody, setHtmlBody] = useState('');
    const [textBody, setTextBody] = useState('');
    const [aggValue, setAggValue] = useState('');
    const [aggUnit, setAggUnit] = useState('Hours');
    const [eligValue, setEligValue] = useState('');
    const [eligUnit, setEligUnit] = useState('Days');
    const [quietStart, setQuietStart] = useState('21:00');
    const [quietEnd, setQuietEnd] = useState('08:00');
    const [timeZone, setTimeZone] = useState('Europe/Brussels');
    const [preview, setPreview] = useState<AdminEmailPreviewResultDto | null>(null);
    const [testTo, setTestTo] = useState('');
    const [outbox, setOutbox] = useState<AdminEmailOutboxListDto[] | null>(null);
    const [outboxStatus, setOutboxStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [testModeEnabled, setTestModeEnabled] = useState(false);
    const [testModeTo, setTestModeTo] = useState('');
    const [testModeSaving, setTestModeSaving] = useState(false);
    // Always start hidden — only 10× title clicks open the panel (no sessionStorage)
    const [testModeRevealed, setTestModeRevealed] = useState(false);
    /** When true, test mode is ON in DB but panel is still collapsed (tiny warning only). */
    const [testModeActiveHint, setTestModeActiveHint] = useState(false);
    const titleClickCount = useRef(0);
    const titleClickResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadList = useCallback(async () => {
        const data = await getAllAdminEmailTemplates();
        setList(data);
    }, []);

    const loadTestMode = useCallback(async () => {
        const mode = await getAdminEmailTestMode();
        const on = !!mode.enabled;
        setTestModeEnabled(on);
        setTestModeTo(mode.toEmail || '');
        // Do NOT auto-open full panel. Only a small hint if already ON (so you can turn it off).
        setTestModeActiveHint(on);
        return mode;
    }, []);

    useEffect(() => {
        // Drop any previous unlock flag from earlier builds
        try {
            sessionStorage.removeItem('admin.emailTestMode.revealed');
        } catch {
            /* ignore */
        }
        loadList().catch((e) => setError(String(e)));
        // Quiet probe: never opens full UI unless ON (hint only)
        loadTestMode().catch(() => { /* ignore if API missing on old backend */ });
    }, [loadList, loadTestMode]);

    useEffect(() => {
        if (user?.email && !testTo) setTestTo(user.email);
        if (user?.email && !testModeTo) setTestModeTo(user.email);
    }, [user?.email, testTo, testModeTo]);

    const handleTitleSecretClick = () => {
        if (testModeRevealed) return;
        if (titleClickResetTimer.current) clearTimeout(titleClickResetTimer.current);
        titleClickCount.current += 1;
        if (titleClickCount.current >= TEST_MODE_REVEAL_CLICKS) {
            titleClickCount.current = 0;
            setTestModeRevealed(true);
            setTestModeActiveHint(false);
            void loadTestMode();
            return;
        }
        // Reset streak if user stops clicking for 2s
        titleClickResetTimer.current = setTimeout(() => {
            titleClickCount.current = 0;
        }, 2000);
    };

    const handleSaveTestMode = async () => {
        setTestModeSaving(true);
        setError(null);
        setMessage(null);
        try {
            const saved = await updateAdminEmailTestMode({
                enabled: testModeEnabled,
                toEmail: testModeTo.trim() || null,
            });
            setTestModeEnabled(!!saved.enabled);
            setTestModeTo(saved.toEmail || '');
            setMessage(
                saved.enabled
                    ? `Email test mode ON — all mail goes to ${saved.toEmail}.`
                    : 'Email test mode OFF — real recipients restored.'
            );
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Failed to save email test mode');
        } finally {
            setTestModeSaving(false);
        }
    };

    const openTemplate = async (key: string) => {
        setSelectedKey(key);
        setMessage(null);
        setError(null);
        setPreview(null);
        setLoading(true);
        try {
            const d = await getAdminEmailTemplate(key);
            setDetail(d);
            const published = d.versions.filter((v) => v.isPublished);
            const preferred =
                published.find((v) => v.locale.toLowerCase() === 'en') ||
                published[0] ||
                d.versions[0];
            if (preferred) {
                setLocale(preferred.locale);
                setSubject(preferred.subjectTemplate);
                setHtmlBody(preferred.htmlBodyTemplate);
                setTextBody(preferred.textBodyTemplate);
            }
            const pol = d.deliveryPolicy;
            setAggValue(pol?.aggregateWindowValue != null ? String(pol.aggregateWindowValue) : '');
            setAggUnit(pol?.aggregateWindowUnit || 'Hours');
            setEligValue(pol?.eligibilityDelayValue != null ? String(pol.eligibilityDelayValue) : '');
            setEligUnit(pol?.eligibilityDelayUnit || 'Days');
            setQuietStart(pol?.quietHoursStartLocal || '21:00');
            setQuietEnd(pol?.quietHoursEndLocal || '08:00');
            setTimeZone(pol?.timeZoneId || 'Europe/Brussels');
        } catch (e: unknown) {
            setError('Failed to load template');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDeliveryPolicy = async () => {
        if (!selectedKey || !detail?.hasDeliveryPolicy) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await updateAdminEmailDeliveryPolicy(selectedKey, {
                aggregateWindowValue: aggValue === '' ? null : Number(aggValue),
                aggregateWindowUnit: aggUnit,
                eligibilityDelayValue: eligValue === '' ? null : Number(eligValue),
                eligibilityDelayUnit: eligUnit,
                quietHoursStartLocal: quietStart || null,
                quietHoursEndLocal: quietEnd || null,
                timeZoneId: timeZone || 'Europe/Brussels',
            });
            setMessage('Delivery policy saved (admin is source of truth for digests).');
            await openTemplate(selectedKey);
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Failed to save delivery policy');
        } finally {
            setLoading(false);
        }
    };

    const loadLocaleVersion = (loc: string) => {
        if (!detail) return;
        setLocale(loc);
        const preferred =
            detail.versions.find((v) => v.isPublished && v.locale.toLowerCase() === loc.toLowerCase()) ||
            detail.versions.find((v) => v.locale.toLowerCase() === loc.toLowerCase());
        if (preferred) {
            setSubject(preferred.subjectTemplate);
            setHtmlBody(preferred.htmlBodyTemplate);
            setTextBody(preferred.textBodyTemplate);
        }
    };

    const handleSave = async () => {
        if (!selectedKey) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await updateAdminEmailTemplateLocale(selectedKey, locale, {
                subjectTemplate: subject,
                htmlBodyTemplate: htmlBody,
                textBodyTemplate: textBody,
            });
            setMessage('Saved (new published version).');
            await openTemplate(selectedKey);
            await loadList();
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Save failed');
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        if (!selectedKey) return;
        setLoading(true);
        setError(null);
        try {
            const result = await previewAdminEmailTemplate(selectedKey, { locale });
            setPreview(result);
        } catch {
            setError('Preview failed');
        } finally {
            setLoading(false);
        }
    };

    const handleTestSend = async () => {
        if (!selectedKey || !testTo.trim()) {
            setError('Enter a test recipient email');
            return;
        }
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            await testSendAdminEmailTemplate(selectedKey, { toEmail: testTo.trim(), locale });
            setMessage(`Test email enqueued to ${testTo.trim()}. Check smtp4dev / outbox.`);
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Test send failed');
        } finally {
            setLoading(false);
        }
    };

    const loadOutbox = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAdminEmailOutbox(outboxStatus || undefined, 100);
            setOutbox(data);
        } catch (e: unknown) {
            console.error(e);
            setError('Failed to load outbox');
            setOutbox([]);
        } finally {
            setLoading(false);
        }
    }, [outboxStatus]);

    // Load when opening Outbox tab or when status filter changes
    useEffect(() => {
        if (tab === 'outbox') {
            void loadOutbox();
        }
    }, [tab, loadOutbox]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1
                    className="select-none text-3xl font-bold text-gray-800"
                    onClick={handleTitleSecretClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTitleSecretClick();
                        }
                    }}
                    role="presentation"
                    title={undefined}
                >
                    Email templates
                </h1>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setTab('templates')}
                        className={`rounded px-4 py-2 text-sm font-medium ${
                            tab === 'templates' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'
                        }`}
                    >
                        Templates
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('outbox')}
                        className={`rounded px-4 py-2 text-sm font-medium ${
                            tab === 'outbox' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'
                        }`}
                    >
                        Outbox
                    </button>
                </div>
            </div>

            <p className="text-sm text-gray-600">
                Platform transactional emails only (auth, ops, trust, lifecycle). Order/shipping mail stays in
                Shopify / SendCloud.
            </p>

            {/* Tiny warning only if redirect is already ON (so it cannot stay hidden forever). Full panel = 10× title. */}
            {!testModeRevealed && testModeActiveHint && (
                <button
                    type="button"
                    onClick={() => {
                        setTestModeRevealed(true);
                        setTestModeActiveHint(false);
                        void loadTestMode();
                    }}
                    className="w-full rounded border border-amber-400 bg-amber-50 px-3 py-2 text-left text-sm text-amber-950"
                >
                    Email redirect is <strong>ON</strong> — click here to manage (or use the title unlock).
                </button>
            )}

            {/* Full test mode UI — only after 10× click on "Email templates" title */}
            {testModeRevealed && (
                <div
                    className={`rounded-lg border p-4 shadow-sm ${
                        testModeEnabled
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-gray-200 bg-white'
                    }`}
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                                Email test mode
                            </h2>
                            <p className="mt-1 text-sm text-gray-600">
                                When enabled, <strong>all</strong> transactional emails (register, password,
                                trust, ops, outgrown, …) are sent to the address below instead of real users.
                                Subjects are prefixed with <code className="text-xs">[TEST → was …]</code>.
                                Turn off before production traffic.
                            </p>
                        </div>
                        <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                            <input
                                type="checkbox"
                                checked={testModeEnabled}
                                onChange={(e) => setTestModeEnabled(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                            />
                            {testModeEnabled ? 'ON' : 'OFF'}
                        </label>
                    </div>
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                        <div className="min-w-[16rem] flex-1">
                            <label className="mb-1 block text-xs font-medium text-gray-600">
                                Redirect all mail to
                            </label>
                            <input
                                type="email"
                                value={testModeTo}
                                onChange={(e) => setTestModeTo(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            />
                        </div>
                        <button
                            type="button"
                            disabled={testModeSaving}
                            onClick={() => void handleSaveTestMode()}
                            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {testModeSaving ? 'Saving…' : 'Save test mode'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setTestModeRevealed(false)}
                            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            Hide
                        </button>
                    </div>
                </div>
            )}

            <ValidationError error={error} />
            {message && (
                <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                    {message}
                </div>
            )}

            {tab === 'templates' && (
                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="rounded-lg border bg-white p-4 shadow-sm lg:col-span-1">
                        <h2 className="mb-3 font-semibold text-gray-800">Templates</h2>
                        {!list && <p className="text-sm text-gray-500">Loading…</p>}
                        <ul className="space-y-2">
                            {list?.map((t) => (
                                <li key={t.templateKey}>
                                    <button
                                        type="button"
                                        onClick={() => void openTemplate(t.templateKey)}
                                        className={`w-full rounded border px-3 py-2 text-left text-sm hover:bg-indigo-50 ${
                                            selectedKey === t.templateKey
                                                ? 'border-indigo-500 bg-indigo-50'
                                                : 'border-gray-200'
                                        }`}
                                    >
                                        <div className="font-medium text-gray-900">{t.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {t.category} · <code>{t.templateKey}</code>
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            locales: {t.locales.join(', ') || '—'}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="rounded-lg border bg-white p-4 shadow-sm lg:col-span-2">
                        {!selectedKey && (
                            <p className="text-sm text-gray-500">Select a template to edit.</p>
                        )}
                        {selectedKey && detail && (
                            <div className="space-y-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">{detail.name}</h2>
                                    <p className="text-sm text-gray-500">{detail.description}</p>
                                    <p className="text-xs text-gray-400">
                                        Key: <code>{detail.templateKey}</code> (not editable)
                                    </p>
                                </div>

                                {detail.hasDeliveryPolicy && (
                                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
                                        <div>
                                            <h3 className="font-semibold text-gray-900">Delivery policy</h3>
                                            <p className="text-xs text-gray-600">
                                                Controls production digests. Values here win over appsettings when set.
                                                Not shown on templates that do not need delayed/batch behaviour.
                                            </p>
                                        </div>
                                        {detail.deliveryPolicy?.showAggregateWindow && (
                                            <div className="flex flex-wrap items-end gap-2">
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium">
                                                        Aggregate / grace window
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        value={aggValue}
                                                        onChange={(e) => setAggValue(e.target.value)}
                                                        className="w-24 rounded border px-2 py-1.5 text-sm"
                                                    />
                                                </div>
                                                <select
                                                    value={aggUnit}
                                                    onChange={(e) => setAggUnit(e.target.value)}
                                                    className="rounded border px-2 py-1.5 text-sm"
                                                >
                                                    <option value="Hours">Hours</option>
                                                    <option value="Days">Days</option>
                                                </select>
                                                <p className="w-full text-xs text-gray-500">
                                                    Collect items this long before one digest email (following).
                                                </p>
                                            </div>
                                        )}
                                        {detail.deliveryPolicy?.showEligibilityDelay && (
                                            <div className="flex flex-wrap items-end gap-2">
                                                <div>
                                                    <label className="mb-1 block text-xs font-medium">
                                                        Eligibility delay
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        value={eligValue}
                                                        onChange={(e) => setEligValue(e.target.value)}
                                                        className="w-24 rounded border px-2 py-1.5 text-sm"
                                                    />
                                                </div>
                                                <select
                                                    value={eligUnit}
                                                    onChange={(e) => setEligUnit(e.target.value)}
                                                    className="rounded border px-2 py-1.5 text-sm"
                                                >
                                                    <option value="Hours">Hours</option>
                                                    <option value="Days">Days</option>
                                                </select>
                                                <p className="w-full text-xs text-gray-500">
                                                    Minimum age before outgrown is eligible (e.g. days since purchase).
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex flex-wrap gap-3">
                                            <div>
                                                <label className="mb-1 block text-xs font-medium">Quiet start</label>
                                                <input
                                                    type="time"
                                                    value={quietStart}
                                                    onChange={(e) => setQuietStart(e.target.value)}
                                                    className="rounded border px-2 py-1.5 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-medium">Quiet end</label>
                                                <input
                                                    type="time"
                                                    value={quietEnd}
                                                    onChange={(e) => setQuietEnd(e.target.value)}
                                                    className="rounded border px-2 py-1.5 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-xs font-medium">Timezone</label>
                                                <input
                                                    type="text"
                                                    value={timeZone}
                                                    onChange={(e) => setTimeZone(e.target.value)}
                                                    className="w-44 rounded border px-2 py-1.5 text-sm"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={loading}
                                            onClick={() => void handleSaveDeliveryPolicy()}
                                            className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                        >
                                            Save delivery policy
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <label className="mb-1 block text-sm font-medium">Locale</label>
                                    <div className="flex flex-wrap gap-2">
                                        {Array.from(
                                            new Set([
                                                ...detail.versions.map((v) => v.locale),
                                                'en',
                                                locale,
                                            ])
                                        ).map((loc) => (
                                            <button
                                                key={loc}
                                                type="button"
                                                onClick={() => loadLocaleVersion(loc)}
                                                className={`rounded px-3 py-1 text-sm ${
                                                    locale === loc
                                                        ? 'bg-indigo-600 text-white'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}
                                            >
                                                {loc}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-medium">Subject</label>
                                    <input
                                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium">HTML body</label>
                                    <textarea
                                        className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-xs"
                                        rows={10}
                                        value={htmlBody}
                                        onChange={(e) => setHtmlBody(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium">Plain text body</label>
                                    <textarea
                                        className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-xs"
                                        rows={6}
                                        value={textBody}
                                        onChange={(e) => setTextBody(e.target.value)}
                                    />
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => void handleSave()}
                                        className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                                    >
                                        Save new version
                                    </button>
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => void handlePreview()}
                                        className="rounded border border-gray-300 px-4 py-2 text-sm font-medium"
                                    >
                                        Preview
                                    </button>
                                </div>

                                <div className="rounded border border-dashed border-gray-300 p-3">
                                    <label className="mb-1 block text-sm font-medium">Test send to</label>
                                    <div className="flex flex-wrap gap-2">
                                        <input
                                            type="email"
                                            className="min-w-[220px] flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                                            value={testTo}
                                            onChange={(e) => setTestTo(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            disabled={loading}
                                            onClick={() => void handleTestSend()}
                                            className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                                        >
                                            Send test
                                        </button>
                                    </div>
                                </div>

                                {preview && (
                                    <div className="rounded border bg-gray-50 p-4">
                                        <h3 className="mb-2 font-semibold">Preview ({preview.locale})</h3>
                                        <p className="mb-2 text-sm">
                                            <strong>Subject:</strong> {preview.subject}
                                        </p>
                                        <div
                                            className="prose max-w-none rounded border bg-white p-3 text-sm"
                                            dangerouslySetInnerHTML={{ __html: preview.htmlBody }}
                                        />
                                        <pre className="mt-3 overflow-auto rounded bg-white p-3 text-xs text-gray-700">
                                            {preview.textBody}
                                        </pre>
                                    </div>
                                )}

                                <div>
                                    <h3 className="mb-2 text-sm font-semibold text-gray-700">Version history</h3>
                                    <ul className="max-h-40 space-y-1 overflow-auto text-xs text-gray-600">
                                        {detail.versions.map((v) => (
                                            <li key={v.id}>
                                                v{v.versionNumber} · {v.locale} ·{' '}
                                                {v.isPublished ? 'published' : 'old'} ·{' '}
                                                {new Date(v.updatedAt).toLocaleString()}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                        {loading && selectedKey && !detail && (
                            <p className="text-sm text-gray-500">Loading template…</p>
                        )}
                    </div>
                </div>
            )}

            {tab === 'outbox' && (
                <div className="rounded-lg border bg-white p-4 shadow-sm">
                    <div className="mb-4 flex flex-wrap items-end gap-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium">Status filter</label>
                            <select
                                className="rounded border border-gray-300 px-3 py-2 text-sm"
                                value={outboxStatus}
                                onChange={(e) => setOutboxStatus(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">All</option>
                                <option value="Pending">Pending</option>
                                <option value="Processing">Processing</option>
                                <option value="Sent">Sent</option>
                                <option value="Failed">Failed</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => void loadOutbox()}
                            disabled={loading}
                            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                        >
                            {loading ? 'Loading…' : 'Refresh'}
                        </button>
                        {outbox && (
                            <span className="pb-2 text-xs text-gray-500">
                                {outbox.length} row{outbox.length === 1 ? '' : 's'}
                                {outboxStatus ? ` · ${outboxStatus}` : ''}
                            </span>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-2 py-2">Id</th>
                                    <th className="px-2 py-2">To</th>
                                    <th className="px-2 py-2">Template</th>
                                    <th className="px-2 py-2">Status</th>
                                    <th className="px-2 py-2">Attempts</th>
                                    <th className="px-2 py-2">Created</th>
                                    <th className="px-2 py-2">Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && outbox === null && (
                                    <tr>
                                        <td colSpan={7} className="px-2 py-6 text-center text-gray-500">
                                            Loading outbox…
                                        </td>
                                    </tr>
                                )}
                                {outbox?.map((row) => (
                                    <tr key={row.id} className="border-b">
                                        <td className="px-2 py-2">{row.id}</td>
                                        <td className="px-2 py-2">{row.toEmail}</td>
                                        <td className="px-2 py-2">
                                            <code className="text-xs">{row.templateKey}</code>
                                        </td>
                                        <td className="px-2 py-2">{row.status}</td>
                                        <td className="px-2 py-2">{row.attempts}</td>
                                        <td className="px-2 py-2 whitespace-nowrap">
                                            {new Date(row.createdAt).toLocaleString()}
                                        </td>
                                        <td
                                            className="max-w-xs truncate px-2 py-2 text-xs text-red-700"
                                            title={row.lastError ?? undefined}
                                        >
                                            {row.lastError}
                                        </td>
                                    </tr>
                                ))}
                                {!loading && outbox && outbox.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-2 py-6 text-center text-gray-500">
                                            No outbox rows
                                            {outboxStatus ? ` with status “${outboxStatus}”` : ''}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplatesPanel;
